import PropTypes from "prop-types";
import qs from "query-string";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import {
  removeBookmark,
  setBookmark,
  setBookmarksPopup,
  setMode,
  setStation,
} from "../model/actions";

import Button from "./controls/Button";
import { Delete, Edit, Eye } from "./icons";

import "./Bookmark.scss";

function Bookmark({ item }) {
  const bookmarks = useSelector((state) => state.bookmarks);
  const stations = useSelector((state) => state.stations);
  const mode = useSelector((state) => state.mode);
  const map = useSelector((state) => state.map);
  const dispatch = useDispatch();
  const inputRef = useRef();
  const [isEditing, setEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(item.title);
  const [warning, setWarning] = useState("");
  const [centerAndZoom, setCenterAndZoom] = useState(null);

  const index = useMemo(() => {
    return bookmarks.indexOf(item);
  }, [bookmarks, item]);

  const edit = useCallback(() => {
    setEditing(!isEditing);
  }, [isEditing]);

  const remove = useCallback(() => {
    dispatch(removeBookmark(item));
  }, [dispatch, item]);

  const load = useCallback(() => {
    window.location.replace(item.path);
  }, [item.path]);

  const onChange = useCallback((evt) => {
    const val = evt.target.value;
    setEditedValue(val);
  }, []);

  const save = useCallback(() => {
    if (!warning && item.title !== editedValue) {
      dispatch(setBookmark({ ...item, title: editedValue }, index));
    }
    setEditing(false);
    setWarning();
  }, [dispatch, editedValue, item, index, warning]);

  // When we start editing we put the focus on the input
  useLayoutEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // We display a warning if the bookmark's name already exist.
  useEffect(() => {
    if (
      isEditing &&
      editedValue &&
      bookmarks.find((bk, idx) => index !== idx && bk.title === editedValue)
    ) {
      setWarning(
        "Diese Favorit schon existiert. Bitte geben Sie ein andere Name.",
      );
    } else {
      setWarning();
    }
  }, [bookmarks, editedValue, index, isEditing]);

  useEffect(() => {
    // When the new mode is set we update center and zoom.
    if (centerAndZoom) {
      const [modeBk, center, zoom] = centerAndZoom;
      if (mode === modeBk) {
        map.getView().setCenter(center);
        map.getView().setZoom(zoom);
        setCenterAndZoom(null);
      }
    }
  }, [centerAndZoom, map, mode]);

  return (
    <div className={`trl-bookmark${isEditing ? " trl-editing" : ""}`}>
      <Button className="trl-bookmark-open" onClick={load} title="Offnen">
        <Eye focusable={false} />
      </Button>
      <div className="trl-bookmark-title">
        {isEditing ? (
          <input
            onBlur={save}
            onChange={onChange}
            onKeyPress={(evt) => {
              if (evt.which === 13) {
                save();
              }
            }}
            ref={inputRef}
            type="text"
            value={editedValue}
          />
        ) : (
          <Button onClick={load}>{item.title}</Button>
        )}

        <div className="trl-warning">{warning}</div>
      </div>
      <div className="trl-bookmark-buttons">
        <Button onClick={edit} title="Editieren">
          <Edit focusable={false} />
        </Button>
        <Button onClick={remove} title="Entfernen">
          <Delete focusable={false} />
        </Button>
      </div>
    </div>
  );
}

Bookmark.propTypes = {
  item: PropTypes.shape({
    path: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default memo(Bookmark);
